import { Link } from "react-router-dom";

// Contatos mostrados nesta pagina. Os enderecos precisam existir de verdade (caixa de entrada ou encaminhamento).
const OPERATOR_NAME = "the SiteSync team";
const PRIVACY_EMAIL = "privacy@sitesync.website";
const SUPPORT_EMAIL = "support@sitesync.website";
const UPDATED = "2 November 2026";

function Section({ title, children }) {
  return (
    <section className="mt-9">
      <h2 className="text-xl font-bold tracking-tight">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-foreground/80">{children}</div>
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="list-disc space-y-2 pl-5">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div
        className="mx-auto max-w-3xl px-5 pb-20"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 2rem)" }}
      >
        <Link to="/" className="text-lg font-bold tracking-tight">
          SiteSync
        </Link>

        <h1 className="mt-8 text-4xl font-bold tracking-tight">Privacy Policy</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: {UPDATED}</p>

        <Section title="Who we are">
          <p>
            SiteSync is a work-management app for construction teams: time tracking, inspection and test plans (ITPs),
            site chat, announcements, daily logs and forms. SiteSync is operated by {OPERATOR_NAME}. You can contact us
            at <a className="underline" href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
          </p>
          <p>
            SiteSync is provided to companies. The company that invited you, or approved your account, decides who has
            access and which work records are kept. We process that company data to run the app for the company.
          </p>
        </Section>

        <Section title="Information we collect">
          <Bullets
            items={[
              "Account and profile: your name, email address, phone number, address, profile photo and role. Your password is stored only as a one-way hash, never in plain text.",
              "Company information: the company name, address, website and logo, and its jobsites with their addresses and map positions.",
              "Time and attendance: clock-in and clock-out times, the jobsite, lunch breaks, shifts and rosters, and timecard approvals and edits (including who changed what).",
              "Location: your device's precise location at the moment you clock in or out, and when a supervisor or admin sets a jobsite's map position using their current location. We ask for it only while the app is open. SiteSync does not track your location in the background.",
              "Work records you create: inspection answers, comments, photos, signatures, attached documents, daily logs, non-conformance reports and forms.",
              "Messages: chat messages and reactions, and announcements you send or receive.",
              "Technical data: our hosting providers log standard request information, such as IP address, device or browser type and time, for security and troubleshooting.",
            ]}
          />
          <p>We do not collect payment card details. SiteSync has no advertising.</p>
        </Section>

        <Section title="Camera, photos and location permissions">
          <p>
            SiteSync asks for permission before it uses your location, camera or photo library. It uses them only when
            you take an action that needs them, for example clocking in, taking a photo for an inspection, or choosing a
            profile picture. You can change these permissions at any time in your phone's Settings, under SiteSync.
            If you do not allow location, you can still use the rest of the app, and you can clock in without location
            when your company allows it.
          </p>
        </Section>

        <Section title="How we use your information">
          <Bullets
            items={[
              "To run the app: sign you in, record attendance, run inspections, deliver messages and keep an audit trail of changes.",
              "To send emails you or your company ask for, such as invitations, password resets and forms you choose to email.",
              "To keep the service secure, prevent misuse and fix problems.",
              "To respond to your questions and to meet legal obligations.",
            ]}
          />
          <p>
            We do not sell personal information. We do not use it for advertising, and we do not track you across other
            apps or websites.
          </p>
        </Section>

        <Section title="Who can see your information">
          <Bullets
            items={[
              "People in your company, depending on their role. Admins and supervisors can see attendance, the locations recorded when people clock in or out, inspections, logs and the list of users. Colleagues see what is shared with them, such as chat, announcements and inspections. People from other companies cannot see your company's data.",
              "Service providers that help us run SiteSync: Render (application hosting), MongoDB Atlas (database), and Resend (sending emails).",
              "Map services: when a map is shown, your device requests map images from OpenStreetMap, which receives your IP address and the map area requested. When a supervisor or admin searches for an address to place a jobsite, that address is sent to Nominatim (OpenStreetMap's search service).",
              "Authorities or other parties when the law requires it.",
            ]}
          />
        </Section>

        <Section title="How long we keep it, and deleting your account">
          <p>We keep company records for as long as the company uses SiteSync.</p>
          <p>
            You can delete your own account inside the app: open the menu (More), tap your name, choose "Delete account"
            and confirm with your password. This removes your name, email, phone, address and photo, and you will no
            longer be able to sign in. Work records of your company, such as attendance, inspections and logs, are kept
            without identifying you. A company can also ask us to delete its data by writing to us.
          </p>
        </Section>

        <Section title="Security">
          <p>
            Data is sent over encrypted connections (HTTPS), passwords are stored as hashes, and access is limited by
            role. No system is completely secure, so please choose a strong password and keep it private.
          </p>
        </Section>

        <Section title="Your choices and rights">
          <p>
            You can see and update your profile in the app. To ask for a copy of your information, to correct it, or to
            ask a question, contact us or your employer. If you are in Australia, you have rights to access and correct
            your personal information under the Privacy Act 1988, and you can complain to the Office of the Australian
            Information Commissioner (oaic.gov.au) if we cannot resolve your concern. Other places have similar rights
            under local law, and we will help you use them.
          </p>
        </Section>

        <Section title="Children">
          <p>SiteSync is a workplace tool and is not directed at children.</p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            If we change this policy we will update the date above, and tell users in the app when the change is
            important.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            SiteSync <br />
            Privacy questions:{" "}
            <a className="underline" href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a> <br />
            Help with the app:{" "}
            <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>
        </Section>
      </div>
    </div>
  );
}
